import { createRestClient, ApiError } from "@marekl/adminer/api";
import { notifySessionExpired } from "../auth/sessionExpiry";

export const apiBaseUrl = window.__CMFIS_CONFIG__?.apiBaseURL ?? "/api";

const client = createRestClient({
  baseUrl: apiBaseUrl,
  // Fires for every 401 the app makes, query or mutation. Without it an
  // expired session was invisible: the request failed, the spinner
  // stopped, and the page went on looking logged in. SessionExpiredModal
  // is the single listener; it only exists inside the authenticated
  // shell, so a rejected login never reaches it.
  onUnauthorized: notifySessionExpired,
});

export { ApiError };
export const apiGet = client.get;
export const apiPost = client.post;
export const apiPut = client.put;
export const apiPatch = client.patch;
export const apiDelete = client.delete;

function readCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "=([^;]*)"),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

// apiPostBlob uploads a raw binary body (e.g. an image) to the given path.
// The JSON rest client can't carry a Blob, so this mirrors its transport
// concerns by hand: same-origin credentials plus the CSRF token echoed
// from the `adminer_csrf` cookie as `X-CSRF-Token`. It throws ApiError on
// a non-2xx response, matching the rest client's contract.
export async function apiPostBlob(path: string, blob: Blob): Promise<void> {
  await postBlob(path, blob);
}

// apiPostBlobJSON is apiPostBlob for endpoints that answer with a body.
// Music upload does: the response carries what analysis measured and
// what it thinks of it, and refetching the whole entry list to learn
// that would be a round trip for data the server just held in its hand.
export async function apiPostBlobJSON<T>(path: string, blob: Blob): Promise<T> {
  const res = await postBlob(path, blob);
  return (await res.json()) as T;
}

// apiPutOnExit sends a JSON PUT that outlives the page (fetch keepalive),
// for a last save on pagehide; the rest client's requests are cancelled with
// the page. Same transport concerns as postBlob. Fire and forget: nobody is
// left to tell how it went.
export function apiPutOnExit(path: string, body: unknown): void {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const csrf = readCookie("adminer_csrf");
  if (csrf) headers["X-CSRF-Token"] = csrf;
  fetch(apiBaseUrl + path, {
    method: "PUT",
    keepalive: true,
    credentials: "include",
    headers,
    body: JSON.stringify(body),
  }).catch(() => {});
}

async function postBlob(path: string, blob: Blob): Promise<Response> {
  const headers: Record<string, string> = {
    "Content-Type": blob.type || "application/octet-stream",
  };
  const csrf = readCookie("adminer_csrf");
  if (csrf) headers["X-CSRF-Token"] = csrf;
  const res = await fetch(apiBaseUrl + path, {
    method: "POST",
    credentials: "include",
    headers,
    body: blob,
  });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, body);
  }
  return res;
}
